import { useState, useEffect, useContext, useRef } from 'react';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ThemeContext } from '../Context';
import { ScrollContext } from '../Context';
import { RubricDragContext } from '../Context';
import GetUserAvatar from '../tool/GetUserAvatar';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';
import { useRubricShareZone, RubricDropOverlay } from './RubricDropZone';
import ChatAttachmentCard from './ChatAttachmentCard';
import axios from 'axios';
import { logAction, formatReportNumber, contentLengthLog, LOG_ACTIONS } from '../../utils/logClient';
import { addRubricsToReport } from '../../utils/reportRubrics';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Paper,
  Fade,
  Alert,
  Tooltip,
  Snackbar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const markMessageAsDeleted = (messages, messageId) =>
  messages.map((msg) => (
    msg._id === messageId ? { ...msg, deleted: true, message: '', attachment: undefined } : msg
  ));

const replaceAttachment = (messages, messageId, attachment) =>
  messages.map((msg) => (
    msg._id === messageId && !msg.deleted ? { ...msg, attachment } : msg
  ));

// Au-delà de cet écart, ou au changement de jour, on rouvre un groupe
// (séparateur d'heure, et nom à nouveau s'il ne s'agit pas de soi).
const MESSAGE_GROUP_GAP_MS = 5 * 60 * 1000;

const areMessagesGrouped = (a, b) => {
  if (!a || !b || a.system || b.system || a.userId !== b.userId) return false;
  const ta = new Date(a.timestamp);
  const tb = new Date(b.timestamp);
  if (Number.isNaN(ta.getTime()) || Number.isNaN(tb.getTime())) return false;
  if (ta.toDateString() !== tb.toDateString()) return false;
  return Math.abs(tb - ta) < MESSAGE_GROUP_GAP_MS;
};

const shouldShowTimeSeparator = (prev, msg) => {
  if (!msg?.timestamp) return false;
  if (!prev?.timestamp) return true;
  const ta = new Date(prev.timestamp);
  const tb = new Date(msg.timestamp);
  if (Number.isNaN(ta.getTime()) || Number.isNaN(tb.getTime())) return true;
  if (ta.toDateString() !== tb.toDateString()) return true;
  return Math.abs(tb - ta) >= MESSAGE_GROUP_GAP_MS;
};

const bubbleRadius = (isCurrentUser, groupedWithPrev, groupedWithNext, size = 18) => {
  const round = `${size}px`;
  const nest = `${Math.max(6, Math.round(size * 0.45))}px`;
  const tail = '4px';
  if (isCurrentUser) {
    if (!groupedWithPrev && groupedWithNext) return `${round} ${tail} ${nest} ${round}`;
    if (groupedWithPrev && groupedWithNext) return `${round} ${nest} ${nest} ${round}`;
    if (groupedWithPrev && !groupedWithNext) return `${round} ${nest} ${round} ${round}`;
    return `${round} ${tail} ${round} ${round}`;
  }
  if (!groupedWithPrev && groupedWithNext) return `${tail} ${round} ${round} ${nest}`;
  if (groupedWithPrev && groupedWithNext) return `${nest} ${round} ${round} ${nest}`;
  if (groupedWithPrev && !groupedWithNext) return `${nest} ${round} ${round} ${round}`;
  return `${tail} ${round} ${round} ${round}`;
};

function Chat() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { theme } = useContext(ThemeContext);
  const { setTargetRubric, triggerScrollToBottom } = useContext(ScrollContext);
  const { startRubricDrag } = useContext(RubricDragContext);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [listenError, setListenError] = useState('');
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  // Rubriques actuelles du document : encadrés « rubrique du document » (numéro, titre, existence)
  const [reportRubrics, setReportRubrics] = useState([]);
  // Encadré dont les rubriques sont en cours d'ajout au document
  const [addingMessageId, setAddingMessageId] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkGuardiaAccess = async () => {
      const access = await GuardiaAccessChecker.checkFeatureAccess('listen');
      setGuardiaAccess(access);
    };
    
    checkGuardiaAccess();
  }, []);

  // Charger les messages existants
  useEffect(() => {
    if (!report) return;

    const fetchMessages = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data && response.data.chat) {
          setMessages(response.data.chat);
        }
        setReportRubrics(Array.isArray(response.data?.rubrics) ? response.data.rubrics : []);
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [report]);

  // Suivre les rubriques du document (ajouts, déplacements, suppressions)
  useEffect(() => {
    if (!socket || !report) return undefined;

    const handleReportUpdated = (data) => {
      if (data?.number === report.number && data?.year === report.year && Array.isArray(data.rubrics)) {
        setReportRubrics(data.rubrics);
      }
    };

    socket.on('reportUpdated', handleReportUpdated);
    return () => socket.off('reportUpdated', handleReportUpdated);
  }, [socket, report]);

  // Écouter les nouveaux messages
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (data) => {
      setMessages((prevMessages) => [...prevMessages, data]);
    };

    const handleMessageDeleted = ({ messageId }) => {
      setMessages((prevMessages) => markMessageAsDeleted(prevMessages, messageId));
    };

    const handleMessageUpdated = ({ messageId, attachment }) => {
      if (attachment) setMessages((prevMessages) => replaceAttachment(prevMessages, messageId, attachment));
    };

    socket.on('newMessage', handleNewMessage);
    socket.on('messageDeleted', handleMessageDeleted);
    socket.on('messageUpdated', handleMessageUpdated);

    return () => {
      socket.off('newMessage', handleNewMessage);
      socket.off('messageDeleted', handleMessageDeleted);
      socket.off('messageUpdated', handleMessageUpdated);
    };
  }, [socket]);

  // Faire défiler automatiquement vers le dernier message (nouveaux messages uniquement, pas lors d'une suppression)
  useEffect(() => {
    if (!loading && messagesEndRef.current && messages.length > previousMessageCountRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    previousMessageCountRef.current = messages.length;
  }, [messages]);

  // Scroll instantané vers le bas quand le chargement initial est terminé
  useEffect(() => {
    if (!loading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
    }
  }, [loading]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          console.warn('Erreur lors de l\'arrêt du MediaRecorder :', error);
        }
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          try {
            track.stop();
          } catch (error) {
            console.warn('Erreur lors de l\'arrêt d\'une piste audio :', error);
          }
        });
        audioStreamRef.current = null;
      }
    };
  }, []);

  const appendTranscriptToMessage = (transcript) => {
    setMessage(prev => {
      const trimmedPrev = prev.trim();
      const combined = trimmedPrev ? `${trimmedPrev} ${transcript}` : transcript;
      return combined.trim();
    });

    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  };

  const guardiaListen = async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      setListenError('Aucun son détecté. Veuillez réessayer.');
      return;
    }

    setListenError('');
    setIsTranscribing(true);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'report-chat-recording.webm');
      formData.append('language', 'fr');

      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/listen', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      let data = null;
      try {
        const clonedResponse = response.clone();
        data = await response.json();
        if (!response.ok && clonedResponse) {
          const rawText = await clonedResponse.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (chat):', rawText);
          }
        }
      } catch (jsonError) {
        console.error('Réponse JSON invalide de guardiaListen (chat):', jsonError);
        try {
          const rawText = await response.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (chat) après échec JSON:', rawText);
          }
        } catch (rawError) {
          console.error('Impossible de lire la réponse brute guardiaListen (chat):', rawError);
        }
      }

      if (!response.ok || !data || !data.success) {
        const errorMessage = data?.message || data?.error || 'Impossible de transcrire l\'audio.';
        throw new Error(errorMessage);
      }

      const transcript = (data.transcript || '').trim();
      if (transcript) {
        appendTranscriptToMessage(transcript);
      } else {
        setListenError('Transcription vide. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur de transcription GuardIA (chat):', error);
      setListenError(error.message || 'Erreur inattendue lors de la transcription.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const stopAudioStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (error) {
          console.warn('Erreur lors de l\'arrêt d\'une piste audio :', error);
        }
      });
      audioStreamRef.current = null;
    }
  };

  const stopListening = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      setIsListening(false);
      try {
        recorder.stop();
      } catch (error) {
        console.warn('Erreur lors de l\'arrêt du MediaRecorder (chat):', error);
      }
    } else {
      setIsListening(false);
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      stopAudioStream();
    }
  };

  const startListening = async () => {
    if (!guardiaAccess.hasAccess) {
      setListenError(guardiaAccess.reason);
      return;
    }

    if (isListening || isTranscribing) {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setListenError('La capture audio n\'est pas supportée par ce navigateur.');
      return;
    }

    try {
      setListenError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      mediaRecorder.addEventListener('error', (event) => {
        console.error('Erreur MediaRecorder (chat):', event.error);
        setListenError(event.error?.message || 'Erreur lors de l\'enregistrement audio.');
        setIsListening(false);
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopAudioStream();
      });

      mediaRecorder.addEventListener('stop', () => {
        setIsListening(false);
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        stopAudioStream();

        if (audioBlob.size === 0) {
          setListenError('Aucun audio détecté. Veuillez réessayer.');
          return;
        }

        guardiaListen(audioBlob);
      });

      mediaRecorder.start();
      setIsListening(true);
    } catch (error) {
      console.error('Erreur lors de l\'activation du micro GuardIA (chat):', error);
      stopAudioStream();
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setIsListening(false);

      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        setListenError('Accès au micro refusé. Veuillez autoriser l\'accès pour utiliser la dictée.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setListenError('Aucun micro n\'a été détecté.');
      } else {
        setListenError(error.message || 'Impossible de démarrer l\'enregistrement audio.');
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !report || !user || !socket) return;

    try {
      const token = sessionStorage.getItem('authToken');

      // Sauvegarder dans la base de données ; le serveur diffuse ensuite le message (avec son _id) via WebSocket
      await axios.post(
        `/api/report/chat/${report.number}/${report.year}`,
        {
          userId: user,
          message: message.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Log du message chat
      logAction('Message chat envoyé', formatReportNumber(report.number, report.year), {
        ...contentLengthLog(message.trim())
      });
      
      setMessage('');
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    }
  };

  const handleDeleteMessage = async (msg) => {
    if (!msg._id || !report) return;

    try {
      const token = sessionStorage.getItem('authToken');
      await axios.delete(
        `/api/report/chat/${report.number}/${report.year}/${msg._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages((prevMessages) => markMessageAsDeleted(prevMessages, msg._id));

      logAction('Message chat supprimé', formatReportNumber(report.number, report.year), {
        ...contentLengthLog(msg.message),
        ...(msg.attachment && { type: msg.attachment.kind, title: msg.attachment.title })
      });
    } catch (error) {
      console.error('Erreur lors de la suppression du message:', error);
    }
  };

  // Publier l'élément déposé dans la discussion : rubrique ou canevas à ajouter, ou rubrique du document
  const shareInChat = async (item) => {
    if (!report || !user) return;

    setIsSharing(true);
    try {
      const attachment = item.kind === 'reportRubric'
        ? { kind: 'reportRubric', rubricId: item.rubricId, title: item.label }
        : { kind: item.kind, title: item.label, rubrics: (await item.getRubrics?.()) || [] };

      if (attachment.rubrics && attachment.rubrics.length === 0) {
        throw new Error('Aucune rubrique à partager');
      }

      // Le serveur diffuse ensuite le message (avec son _id) via WebSocket
      const token = sessionStorage.getItem('authToken');
      await axios.post(
        `/api/report/chat/${report.number}/${report.year}`,
        { userId: user, attachment },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      logAction(LOG_ACTIONS.CHAT_SHARE, formatReportNumber(report.number, report.year), {
        type: attachment.kind,
        title: attachment.title,
        rubricsCount: attachment.rubrics ? attachment.rubrics.length : 1
      });
    } catch (error) {
      console.error('Erreur lors du partage dans la discussion:', error);
      setNotice({ open: true, severity: 'error', message: 'Impossible de partager cet élément dans la discussion.' });
    } finally {
      setIsSharing(false);
    }
  };

  const { dragItem, isOver: isShareOver, dropZoneProps: shareZoneProps } = useRubricShareZone(
    report && user && !loading ? shareInChat : null
  );

  // L'encadré d'une rubrique ajoutée mène désormais à celle-ci, pour tous les participants
  const linkSharedRubric = async (msg, rubricId) => {
    setMessages((prevMessages) => replaceAttachment(prevMessages, msg._id, { ...msg.attachment, addedRubricId: rubricId }));
    try {
      const token = sessionStorage.getItem('authToken');
      await axios.patch(
        `/api/report/chat/${report.number}/${report.year}/${msg._id}/added-rubric`,
        { rubricId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Erreur lors du rattachement de l\'encadré à la rubrique ajoutée:', error);
    }
  };

  // Ajouter au document les rubriques d'un encadré, avant beforeRubricId (en fin de document si null)
  const addSharedRubrics = async (msg, beforeRubricId = null) => {
    const { attachment } = msg;
    if (!report || !attachment?.rubrics?.length || addingMessageId) return;

    setAddingMessageId(msg._id);
    try {
      const { createdRubrics, updatedRubrics, insertedPosition, atEnd } = await addRubricsToReport({
        report,
        socket,
        rubrics: attachment.rubrics.map(({ title, content, version, generated }) => ({
          title,
          content,
          version,
          generated
        })),
        beforeRubricId
      });

      logAction(LOG_ACTIONS.RUBRIC_ADD_FROM_CHAT, formatReportNumber(report.number, report.year), {
        type: attachment.kind,
        title: attachment.title,
        sharedBy: msg.userId,
        rubricsCount: createdRubrics.length,
        rubricTitles: createdRubrics.map(rubric => rubric.title),
        position: insertedPosition
      });

      setReportRubrics(updatedRubrics);
      if (attachment.kind === 'rubric' && createdRubrics.length === 1 && msg._id) {
        linkSharedRubric(msg, createdRubrics[0].id);
      }

      if (atEnd) triggerScrollToBottom();
      setNotice({
        open: true,
        severity: 'success',
        message: attachment.kind === 'standard'
          ? `Canevas « ${attachment.title} » ajouté au document`
          : `Rubrique « ${attachment.title} » ajoutée au document`
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout au document depuis la discussion:', error);
      setNotice({ open: true, severity: 'error', message: 'Erreur lors de l\'ajout au document.' });
    } finally {
      setAddingMessageId(null);
    }
  };

  const closeNotice = () => setNotice(prev => ({ ...prev, open: false }));

  const handleAttachmentDragStart = (event, msg) => {
    const { attachment } = msg;
    startRubricDrag(event, {
      kind: attachment.kind,
      label: attachment.title,
      count: attachment.rubrics?.length || 0,
      origin: 'chat',
      insert: (beforeRubricId) => addSharedRubrics(msg, beforeRubricId)
    });
  };

  const viewSharedRubric = (rubric) => {
    logAction(LOG_ACTIONS.RUBRIC_VIEW, formatReportNumber(report?.number, report?.year), {
      rubricId: rubric.id,
      rubricTitle: rubric.title,
      source: 'discussion'
    });
    setTargetRubric(rubric.id);
  };

  const sortedReportRubrics = [...reportRubrics].sort((a, b) => a.position - b.position);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    // Pour les messages plus anciens, afficher la date complète
    return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
  };

  const toggleMicrophone = () => {
    if (!guardiaAccess.hasAccess) {
      alert(guardiaAccess.reason);
      return;
    }
    
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <Box 
      {...shareZoneProps}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: isDark ? '#1a252f' : '#f8f9fa',
      }}
    >
      {isShareOver && (
        <RubricDropOverlay
          isDark={isDark}
          zIndex={12}
          message={`Partager « ${dragItem?.label || ''} » dans la discussion`}
        />
      )}

      {/* Zone des messages */}
      <Box 
        ref={messagesContainerRef}
        sx={{ 
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - 70px)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress 
              size={30} 
              sx={{
                color: isDark ? '#4fc3f7' : 'primary.main'
              }}
            />
          </Box>
        ) : messages.length === 0 ? (
          <Fade in={true} timeout={800}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%'
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : 'rgba(25, 118, 210, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                animation: 'emptyPulse 2s ease-in-out infinite',
                '@keyframes emptyPulse': {
                  '0%': {
                    transform: 'scale(1)',
                    boxShadow: isDark 
                      ? '0 0 0 0 rgba(79, 195, 247, 0.4)' 
                      : '0 0 0 0 rgba(25, 118, 210, 0.3)',
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                    boxShadow: isDark 
                      ? '0 0 20px 10px rgba(79, 195, 247, 0)' 
                      : '0 0 20px 10px rgba(25, 118, 210, 0)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                    boxShadow: isDark 
                      ? '0 0 0 0 rgba(79, 195, 247, 0)' 
                      : '0 0 0 0 rgba(25, 118, 210, 0)',
                  },
                },
              }}>
                <SendIcon sx={{ 
                  fontSize: 36, 
                  color: isDark ? '#4fc3f7' : 'primary.main',
                  opacity: 0.8
                }} />
              </Box>
              <Typography 
                variant="body1" 
                sx={{
                  color: isDark ? '#bdc3c7' : 'text.secondary',
                  fontWeight: 500,
                  mb: 0.5
                }}
              >
                Aucun message
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isDark ? '#7f8c8d' : 'text.disabled',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}
              >
                Lancez la conversation !
              </Typography>
            </Box>
          </Fade>
        ) : (
          <List sx={{ width: '100%', p: 0, mb: 2 }}>
            {
              messages.map((msg, index) => {
                if (msg.system) {
                  return (
                    <Fade in={true} key={msg._id || index} timeout={300}>
                      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center', px: 1 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          columnGap: 0.5,
                          maxWidth: '90%',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '12px',
                          textAlign: 'center',
                          bgcolor: isDark ? 'rgba(79, 195, 247, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          color: isDark ? '#95a5a6' : 'text.secondary',
                        }}>
                          <Box sx={{ typography: 'caption', fontWeight: 600, color: isDark ? '#4fc3f7' : 'primary.main' }}>
                            <GetUserAvatar matricule={msg.userId} defaultValue="..." />
                          </Box>
                          <Typography component="span" variant="caption" sx={{ color: 'inherit', wordBreak: 'break-word' }}>
                            {msg.message}
                          </Typography>
                          <Typography component="span" variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
                            · {formatDate(msg.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                    </Fade>
                  );
                }

                const isCurrentUser = msg.userId === user;
                const attachment = !msg.deleted ? msg.attachment : null;
                const linkedRubricId = attachment?.kind === 'reportRubric' ? attachment.rubricId : attachment?.addedRubricId;
                const linkedIndex = linkedRubricId
                  ? sortedReportRubrics.findIndex(rubric => rubric.id === linkedRubricId)
                  : -1;
                const linkedRubric = linkedIndex !== -1 ? sortedReportRubrics[linkedIndex] : null;
                const previous = index > 0 ? messages[index - 1] : null;
                const next = index < messages.length - 1 ? messages[index + 1] : null;
                const groupedWithPrev = areMessagesGrouped(previous, msg);
                const groupedWithNext = areMessagesGrouped(msg, next);
                const showTimeSeparator = shouldShowTimeSeparator(previous, msg);
                // Le sien se reconnaît à l'alignement : on ne rappelle son nom que pour les autres,
                // et seulement en tête de série.
                const showAuthor = !isCurrentUser && !groupedWithPrev;
                const exactTime = msg.timestamp
                  ? format(new Date(msg.timestamp), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })
                  : '';
                return (
                  <Fade in={true} key={msg._id || index} timeout={300}>
                    <Box sx={{ 
                      mb: groupedWithNext ? 0.35 : 1.25,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                      '&:hover .chat-delete-btn, & .chat-delete-btn:focus-visible': {
                        opacity: 1,
                      },
                    }}>
                      {showTimeSeparator && (
                        <Typography
                          variant="caption"
                          sx={{
                            alignSelf: 'center',
                            width: '100%',
                            textAlign: 'center',
                            mt: index === 0 ? 0.25 : 0.75,
                            mb: 0.75,
                            color: isDark ? '#7f8c8d' : 'text.disabled',
                            fontSize: '0.7rem',
                            letterSpacing: '0.01em',
                          }}
                        >
                          {formatDate(msg.timestamp)}
                        </Typography>
                      )}

                      {showAuthor && (
                        <Box sx={{
                          mb: 0.35,
                          px: 0.75,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          lineHeight: 1.2,
                          color: isDark ? '#4fc3f7' : 'primary.main',
                        }}>
                          <GetUserAvatar matricule={msg.userId} defaultValue="..." />
                        </Box>
                      )}

                      <Box
                        sx={{
                          position: 'relative',
                          maxWidth: attachment ? '90%' : '80%',
                          ...(attachment && { width: '90%' })
                        }}
                      >
                        {isCurrentUser && msg._id && !msg.deleted && (
                          <Tooltip title="Supprimer le message">
                            <IconButton
                              className="chat-delete-btn"
                              size="small"
                              aria-label="Supprimer le message"
                              onClick={() => handleDeleteMessage(msg)}
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                right: 'calc(100% + 2px)',
                                transform: 'translateY(-50%)',
                                p: 0.3,
                                opacity: 0,
                                transition: 'opacity 0.2s ease, color 0.2s ease',
                                color: isDark ? '#5d6d7e' : '#bbb',
                                '&:hover': { color: '#e74c3c' }
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {attachment ? (
                          <ChatAttachmentCard
                            attachment={attachment}
                            linkedRubric={linkedRubric}
                            linkedNumber={linkedIndex + 1}
                            isCurrentUser={isCurrentUser}
                            isDark={isDark}
                            adding={addingMessageId === msg._id}
                            onAdd={() => addSharedRubrics(msg)}
                            onView={() => linkedRubric && viewSharedRubric(linkedRubric)}
                            onDragStart={(event) => handleAttachmentDragStart(event, msg)}
                            borderRadius={bubbleRadius(isCurrentUser, groupedWithPrev, groupedWithNext, 14)}
                          />
                        ) : (
                          <Paper
                            elevation={0}
                            title={exactTime || undefined}
                            sx={{
                              p: 1.5,
                              bgcolor: msg.deleted
                                ? 'transparent'
                                : isCurrentUser 
                                  ? (isDark ? '#4fc3f7' : 'primary.light')
                                  : (isDark ? '#22303e' : 'white'),
                              color: msg.deleted
                                ? (isDark ? '#95a5a6' : 'text.secondary')
                                : isCurrentUser 
                                  ? (isDark ? '#000' : 'primary.contrastText')
                                  : (isDark ? '#ecf0f1' : 'text.primary'),
                              borderRadius: bubbleRadius(isCurrentUser, groupedWithPrev, groupedWithNext),
                              boxShadow: msg.deleted
                                ? 'none'
                                : isDark 
                                  ? '0 1px 2px rgba(0,0,0,0.3)' 
                                  : '0 1px 2px rgba(0,0,0,0.08)',
                              position: 'relative',
                              zIndex: 1,
                              border: msg.deleted ? '1px dashed' : '1px solid',
                              borderColor: msg.deleted
                                ? (isDark ? '#3d5a73' : 'rgba(0,0,0,0.15)')
                                : isCurrentUser 
                                  ? (isDark ? '#4fc3f7' : 'primary.light')
                                  : (isDark ? '#2d3f4c' : 'rgba(0,0,0,0.08)'),
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                wordBreak: 'break-word', 
                                lineHeight: 1.5,
                                color: 'inherit',
                                fontStyle: msg.deleted ? 'italic' : 'normal'
                              }}
                            >
                              {msg.deleted ? (
                                <>
                                  <DeleteOutlineIcon sx={{ fontSize: '1rem', verticalAlign: 'text-bottom', mr: 0.5 }} />
                                  Message supprimé
                                </>
                              ) : msg.message}
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                    </Box>
                  </Fade>
                );
              })
            }
            <div ref={messagesEndRef} />
          </List>
        )}
      </Box>

      {/* Indicateur de partage (canevas : lecture du catalogue avant publication) */}
      {isSharing && !isListening && !isTranscribing && (
        <Box sx={{
          position: 'absolute',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 11,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          mb: 1,
          borderRadius: '20px',
          backgroundColor: isDark ? 'rgba(79, 195, 247, 0.12)' : 'rgba(25, 118, 210, 0.08)',
          backdropFilter: 'blur(8px)',
          border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.25)' : 'rgba(25, 118, 210, 0.2)'}`,
        }}>
          <CircularProgress size={12} sx={{ color: isDark ? '#4fc3f7' : 'primary.main' }} />
          <Typography variant="caption" sx={{ fontWeight: 500, color: isDark ? '#4fc3f7' : 'primary.main' }}>
            Partage en cours...
          </Typography>
        </Box>
      )}

      {/* Indicateur d'écoute du micro */}
      {(isListening || isTranscribing) && (
        <Box sx={{
          position: 'absolute',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 11,
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: listenError ? 0 : 1,
            p: 0.75,
            borderRadius: '20px',
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)'}`,
            boxShadow: isDark
              ? '0 2px 8px rgba(244, 67, 54, 0.15)'
              : '0 2px 8px rgba(244, 67, 54, 0.08)',
          }}>
            <Box sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#f44336',
              mr: 1,
              animation: isListening ? 'recordingPulse 1.5s infinite' : 'none',
              '@keyframes recordingPulse': {
                '0%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.3)', opacity: 0.7 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: isDark ? '#ff8a80' : '#d32f2f',
                fontWeight: 500,
                letterSpacing: '0.02em',
              }}
            >
              {isListening ? 'Écoute en cours...' : 'Transcription en cours...'}
            </Typography>
          </Box>
        </Box>
      )}

      {listenError && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 11,
            width: '90%'
          }}
        >
          <Alert
            severity="error"
            sx={{
              borderRadius: '12px',
              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)',
              color: isDark ? '#ff8a80' : 'error.main',
              '& .MuiAlert-icon': {
                color: isDark ? '#ff8a80' : 'error.main'
              }
            }}
          >
            {listenError}
          </Alert>
        </Box>
      )}

      {/* Zone d'écriture fixée en bas */}
      <Paper
        component="form"
        onSubmit={handleSendMessage}
        elevation={3}
        sx={{
          p: 1.5,
          backgroundColor: isDark ? '#22303e' : 'white',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Votre message..."
          size="small"
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
          sx={{
            mr: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '25px',
              backgroundColor: isDark ? '#1a252f' : '#f5f7f9',
              color: isDark ? '#ecf0f1' : 'inherit',
              transition: 'all 0.2s',
              '&:hover, &.Mui-focused': {
                backgroundColor: isDark ? '#22303e' : '#fff',
                boxShadow: isDark
                  ? '0 0 0 2px rgba(79, 195, 247, 0.1)'
                  : '0 0 0 2px rgba(25, 118, 210, 0.1)',
              },
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              },
              '&.Mui-focused fieldset': {
                borderColor: isDark ? '#4fc3f7' : 'primary.main',
              },
            },
            '& .MuiInputBase-input': {
              color: isDark ? '#ecf0f1' : 'inherit',
              '&::placeholder': {
                color: isDark ? '#95a5a6' : 'inherit',
                opacity: 1
              }
            }
          }}
        />
        <IconButton
          onClick={toggleMicrophone}
          disabled={!guardiaAccess.hasAccess || (isTranscribing && !isListening)}
          sx={{
            p: 1,
            mr: 1,
            borderRadius: '50%',
            backgroundColor: !guardiaAccess.hasAccess
              ? (isDark ? '#555' : '#ccc')
              : isListening
              ? (isDark ? '#ff5722' : '#f44336')
              : (isDark ? '#2d3f4c' : 'grey.100'),
            color: !guardiaAccess.hasAccess
              ? (isDark ? '#7f8c8d' : 'grey.400')
              : isListening
              ? '#fff'
              : (isDark ? '#95a5a6' : 'grey.600'),
            transition: 'all 0.2s ease',
            boxShadow: isListening ? '0 0 0 2px rgba(244, 67, 54, 0.3)' : 'none',
            animation: isListening ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
              },
              '50%': {
                transform: 'scale(1.1)',
                boxShadow: '0 0 0 4px rgba(244, 67, 54, 0.2)',
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
              },
            },
            '&:hover': !guardiaAccess.hasAccess ? {} : {
              backgroundColor: isListening
                ? (isDark ? '#d84315' : '#d32f2f')
                : (isDark ? '#5a6c7d' : 'grey.200'),
              transform: isListening ? 'scale(1.05)' : 'scale(1.05)',
            },
            '&.Mui-disabled': {
              backgroundColor: isDark ? '#555' : '#ccc',
              color: isDark ? '#7f8c8d' : 'grey.400'
            }
          }}
        >
          {isListening ? <MicOffIcon /> : (isTranscribing ? <CircularProgress size={18} sx={{ color: isDark ? '#95a5a6' : 'grey.600' }} /> : <MicIcon />)}
        </IconButton>
        <IconButton
          type="submit"
          disabled={!message.trim()}
          sx={{
            p: 1,
            borderRadius: '50%',
            backgroundColor: message.trim()
              ? (isDark ? '#4fc3f7' : 'primary.main')
              : (isDark ? '#2d3f4c' : 'grey.100'),
            color: message.trim()
              ? (isDark ? '#000' : 'white')
              : (isDark ? '#7f8c8d' : 'grey.400'),
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: message.trim()
                ? (isDark ? '#29b6f6' : 'primary.dark')
                : (isDark ? '#5a6c7d' : 'grey.200'),
              transform: message.trim() ? 'scale(1.05)' : 'none',
            },
          }}
        >
          <SendIcon />
        </IconButton>
        </Box>
      </Paper>

      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        onClose={(event, reason) => {
          if (reason !== 'clickaway') closeNotice();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeNotice}
          severity={notice.severity}
          sx={{ width: '100%', fontSize: '0.8rem', py: 0.5 }}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Chat;



